import { ValueRendererProps } from '@src/components/combobox/Combobox';
import { CustomTooltip } from '@src/components/tooltip/Tooltip';
import { Icon } from '@src/components/icon/Icon';
import { cn } from '@/lib/utils';
import { LinkBreak2Icon } from '@radix-ui/react-icons';
import type { ArticleReference } from '@src/scenario-editor/providers/fetch.context';
import { REFERENCE_BADGES, REFERENCE_REASONS } from '@src/components/dynamicSelector/articleReferences';

interface FilteredIdsRendererProps extends ValueRendererProps {
  // LF360-2023: what the selected ids missing from `options` point at, by id
  references?: Record<string, ArticleReference>;
}

export const FilteredIdsRenderer = ({value, onValueChange, options, children, disabled, references}: FilteredIdsRendererProps) => {
  return (
    <div className="w-full">
      {(value || []).map((option) => {
        const isValueValid = options?.find((opt) => opt.value === option.value);
        const reference = isValueValid ? undefined : references?.[option.value];
        const badge = reference ? REFERENCE_BADGES[reference.state] : undefined;
        const invalidReason = (reference && REFERENCE_REASONS[reference.state]) ?? 'The associated object was deleted';
        return (
          <div className="mr-2 my-1 px-4 py-1 w-full flex items-center justify-between" key={option.value}>
            <CustomTooltip content={isValueValid ? option.label : invalidReason}>
              <div className="flex items-center font-light min-w-0">
                {!isValueValid && <LinkBreak2Icon className="text-red-500 mr-2 shrink-0"/>}
                <span className="line-clamp-2">{option.label}</span>
                {badge && (
                  <span className="ml-2 shrink-0 rounded-sm border border-red-200 bg-red-50 px-1.5 text-xs text-red-600">
                    {badge}
                  </span>
                )}
              </div>
            </CustomTooltip>
            {!disabled && (
              <CustomTooltip content="Remove answer">
                <button
                  onClick={() => {
                    onValueChange?.((value || []).filter((opt) => opt.value !== option.value));
                  }}
                >
                  <Icon type="delete"
                        className={cn(
                          "ml-1 cursor-pointer max-w-3 min-w-3",
                        )}
                  />
                </button>
              </CustomTooltip>
            )}
          </div>
        )}
      )}
      <div className="border-t border-input py-3 pl-2">
        {children}
      </div>
    </div>
  );
};
